import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import StickyBooking from '../../components/feature/StickyBooking';
import { pathFor, type Lang } from '../../router/paths';

export default function HotelPage() {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  const openLightbox = (src: string) => { setLightboxImage(src); setLightboxOpen(true); };
  const closeLightbox = () => { setLightboxOpen(false); setLightboxImage(''); };

  // Kini-Modal (König Ludwig II.) – identisch zur Startseite
  const [kiniOpen, setKiniOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const openKini = () => {
    setKiniOpen(true);
    if (!audioRef.current) {
      audioRef.current = new Audio('https://upload.wikimedia.org/wikipedia/commons/7/7e/Bayerischer_Defiliermarsch.ogg');
      audioRef.current.volume = 0.4;
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  const closeKini = () => {
    setKiniOpen(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    if (!kiniOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeKini();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [kiniOpen]);

  const scrollToKarriere = () => {
    const el = document.getElementById('karriere');
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
  };

  const galleryImages: { src: string; alt: string }[] = [
    { src: '/images/ambiente/Aussen_Frontansicht_Fruehling.jpg',                  alt: 'Hotel Alpenhof Gauting im Frühling – Außenansicht mit Garten' },
    { src: '/images/ambiente/Aussen_Frontansicht_Winter.jpg',                     alt: 'Hotel Alpenhof Gauting im Winter – verschneite Außenansicht' },
    { src: '/images/ambiente/Aussen_Frontansicht_blaue_Stunde.jpg',               alt: 'Hotel Alpenhof Gauting zur blauen Stunde – Außenansicht bei Dämmerung' },
    { src: '/images/ambiente/Aussen_Seiteneingang_Gaeste.jpg',                    alt: 'Seiteneingang für Gäste – Hotel Alpenhof Gauting' },
    { src: '/images/ambiente/Aussen_Gaesteeingang_Garten.jpg',                    alt: 'Gästeeingang mit Garten – Hotel Alpenhof Gauting' },
    { src: '/images/ambiente/Aussen_Garten_Sommer.jpg',                           alt: 'Garten im Sommer – Hotel Alpenhof Gauting bei München' },
    { src: '/images/ambiente/Fruehstueck_Buffetraum_2025.jpg',                    alt: 'Frühstücksbuffet mit Bio-Produkten – Hotel Alpenhof Gauting' },
    { src: '/images/ambiente/Fruehstuecksraum_2025.jpg',                          alt: 'Frühstücksraum mit Tageslicht – Hotel Alpenhof Gauting' },
    { src: '/images/ambiente/Garten_Fruehjahr_1_2015.jpg',                        alt: 'Garten im Frühling – Hotel Alpenhof Gauting im Würmtal' },
    { src: '/images/ambiente/Kachelofenstube_Sitzecke_mit_Bibliothek_2025.jpg',   alt: 'Kachelofenstube mit Bibliothek – Hotel Alpenhof Gauting' },
  ];

  const values = [
    { icon: 'ri-heart-3-line',     title: t('hotelVal1Title'), text: t('hotelVal1Text') },
    { icon: 'ri-star-line',        title: t('hotelVal2Title'), text: t('hotelVal2Text') },
    { icon: 'ri-map-pin-2-line',   title: t('hotelVal3Title'), text: t('hotelVal3Text') },
  ];

  const breakfastFeatures = [
    { img: '/images/ambiente/Fruehstueck_innen_2025.jpg',         imgAlt: 'Frühstücksraum innen – Hotel Alpenhof Gauting',           icon: 'ri-restaurant-line', title: t('hotelBfFeat1Title'), text: t('hotelBfFeat1Text') },
    { img: '/images/Service.jpg',                                 imgAlt: 'Persönlicher Frühstücksservice – Hotel Alpenhof Gauting', icon: 'ri-heart-line',      title: t('hotelBfFeat2Title'), text: t('hotelBfFeat2Text') },
    { img: '/images/ambiente/Kachelofenstube_Eckbank_2025.jpg',   imgAlt: 'Kachelofenstube mit Eckbank – Hotel Alpenhof Gauting',    icon: 'ri-goblet-line',     title: t('hotelBfFeat3Title'), text: t('hotelBfFeat3Text') },
  ];

  const jobs = [
    { title: t('hotelJob1Title'), details: t('hotelJob1Details') },
    { title: t('hotelJob2Title'), details: t('hotelJob2Details') },
    { title: t('hotelJob3Title'), details: t('hotelJob3Details') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <section className="relative h-[75vh] sm:h-[80vh] sm:min-h-[640px] flex items-end sm:items-center justify-center overflow-hidden pb-12 sm:pb-0">
        <div className="absolute inset-0">
          <img
            src="/images/ambiente/Aussenansicht_Logo_Steingarten_Fruehling.jpg"
            alt="Hotel Alpenhof Gauting im Frühling – Außenansicht mit Logo und Steingarten"
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover object-top"
          />
          {/* Mobile-Overlay etwas dunkler unten für bessere Lesbarkeit der Copy */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent sm:from-transparent sm:via-transparent sm:to-transparent"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 text-left sm:pt-44">
          <div className="max-w-5xl mx-auto text-white">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-3 sm:mb-4 text-amber-300">{t('hotelHeroKicker')}</p>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-serif leading-snug sm:leading-tight mb-3 sm:mb-6">
              {t('hotelHeroTitle1')}<br />{t('hotelHeroTitle2')}<br /><span className="text-amber-200">{t('hotelHeroTitle3')}</span>
            </h1>
            <p className="text-sm sm:text-lg text-white/80 max-w-xl leading-relaxed mb-4 sm:mb-6">
              {t('hotelHeroLead')}
            </p>

            <button
              onClick={scrollToKarriere}
              className="sm:hidden inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full text-white text-xs font-semibold px-4 py-2 hover:bg-white/25 transition-colors cursor-pointer"
            >
              <span className="text-amber-300">✦</span>
              {t('hotelHeroCareerInline')}
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        <button
          onClick={scrollToKarriere}
          className="hidden sm:inline-flex absolute bottom-8 right-6 sm:right-10 flex-col items-start bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-4 text-left hover:bg-white/20 transition-all duration-200 cursor-pointer shadow-xl z-10"
        >
          <span className="text-amber-300 text-base mb-1">{t('hotelHeroCareerCardKicker')}</span>
          <span className="text-white text-xs leading-snug">{t('hotelHeroCareerCardText')}</span>
        </button>
      </section>

      <section className="py-24 sm:py-32 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-serif text-stone-900 mb-6">{t('hotelTradTitle')}</h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-6">
            {t('hotelTradText1')}
          </p>
          <p className="text-lg text-stone-600 leading-relaxed">
            {t('hotelTradText2')}
          </p>
        </div>
      </section>

      <div className="w-full h-64 sm:h-96 overflow-hidden">
        <img
          src="/images/ambiente/Fruehstueck_Garten_4_2025.jpg"
          alt="Frühstück im Garten – Hotel Alpenhof Gauting"
          loading="lazy"
          className="w-full h-full object-cover object-center"
        />
      </div>

      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-4 text-amber-700">{t('hotelValuesKicker')}</p>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">{t('hotelValuesTitle')}</h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
              {t('hotelValuesLead')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {values.map(({ icon, title, text }) => (
              <div key={title} className="flex gap-4 items-start border-t border-stone-200 pt-6">
                <i className={`${icon} text-xl text-amber-700 flex-shrink-0 mt-0.5`}></i>
                <div>
                  <h3 className="text-lg font-serif text-stone-900 mb-2">{title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-4 text-amber-700">{t('hotelBfKicker')}</p>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">
              {(() => {
                const parts = t('hotelBfTitle').split('Kini');
                if (parts.length < 2) return t('hotelBfTitle');
                return (
                  <>
                    {parts[0]}
                    <span className="relative inline-block cursor-pointer" onClick={openKini}>
                      <span className="relative">
                        Kini
                        <span className="absolute bottom-0 left-0 w-full h-[3px] bg-amber-600 rounded-full"></span>
                      </span>
                    </span>
                    {parts[1]}
                  </>
                );
              })()}
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
              {t('hotelBfLead')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {breakfastFeatures.map(({ img, imgAlt, icon, title, text }) => (
              <div key={title} className="group bg-stone-50 rounded-2xl overflow-hidden border border-stone-100 hover:shadow-lg transition-shadow duration-300">
                <div className="relative aspect-[3/2] overflow-hidden">
                  <img src={img} alt={imgAlt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 sm:p-7">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-full shrink-0">
                      <i className={`${icon} text-amber-700 text-base`}></i>
                    </div>
                    <h3 className="text-base font-serif font-semibold text-stone-900">{title}</h3>
                  </div>
                  <p className="text-stone-600 text-sm leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-4 text-amber-700">{t('hotelGalleryKicker')}</p>
            <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">{t('hotelGalleryTitle')}</h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
              {t('hotelGalleryLead')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative overflow-hidden rounded-2xl cursor-pointer group aspect-[3/2]"
                onClick={() => openLightbox(image.src)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-3xl text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 sm:py-32 px-6 overflow-hidden bg-amber-700">
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-serif mb-6">{t('hotelCtaTitle')}</h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto">
            {t('hotelCtaText')}
          </p>
          <Link
            to={pathFor('buchen', lang)}
            onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
            className="inline-flex items-center gap-2 bg-white text-amber-700 px-8 py-3 rounded-md font-semibold hover:bg-amber-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            {t('hotelCtaButton')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <section id="karriere" className="py-16 px-6 bg-white border-t border-stone-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-3 text-amber-700">{t('hotelCareerKicker')}</p>
            <h2 className="text-3xl font-serif text-stone-900 mb-4">{t('hotelCareerTitle')}</h2>
            <p className="text-stone-500 leading-relaxed">
              {t('hotelCareerLead')}
            </p>
          </div>

          <div className="space-y-3">
            {jobs.map((job) => (
              <div key={job.title} className="flex items-center justify-between gap-4 border border-stone-200 rounded-xl px-5 py-4 hover:border-amber-300 hover:shadow-sm transition-all duration-200">
                <div>
                  <p className="text-sm font-semibold text-stone-800">{job.title}</p>
                  <p className="text-xs text-stone-400 mt-0.5">{job.details}</p>
                </div>
                <a
                  href="mailto:hotel@alpenhof-gauting.de?subject=Bewerbung"
                  className="shrink-0 text-xs font-medium text-amber-700 border border-amber-300 rounded-full px-4 py-1.5 hover:bg-amber-700 hover:text-white hover:border-amber-700 transition-all duration-200 whitespace-nowrap"
                >
                  {t('hotelCareerApply')}
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-stone-400 mt-6">
            {t('hotelCareerNoFitText')}{' '}
            <a href="mailto:hotel@alpenhof-gauting.de" className="text-amber-700 hover:underline">
              {t('hotelCareerNoFitLink')}
            </a>
          </p>
        </div>
      </section>

      <Footer />
      <StickyBooking />

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            onClick={closeLightbox}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={lightboxImage}
            alt="Hotel Alpenhof Gauting – Detailansicht"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {kiniOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-4"
          onClick={closeKini}
          style={{ animation: 'kiniFadeIn 0.25s ease-out both' }}
        >
          <div
            className="relative bg-white shadow-2xl overflow-hidden flex flex-col absolute inset-x-0 bottom-0 max-h-[90vh] rounded-t-2xl sm:static sm:inset-auto sm:max-h-none sm:rounded-2xl sm:max-w-3xl sm:w-full"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'kiniPopIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both' }}
          >
            <button
              onClick={closeKini}
              aria-label={t('close')}
              className="absolute top-3 right-3 z-10 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-gray-700 hover:text-gray-900 transition-colors shadow-md flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 overflow-y-auto">
              <div className="relative h-72 sm:h-80 md:h-auto flex-shrink-0 bg-stone-100">
                <img
                  src="/images/hotel/Koenig_Ludwig_II.webp"
                  width={800}
                  height={1200}
                  loading="lazy"
                  alt="König Ludwig II."
                  className="w-full h-full object-contain md:object-cover md:object-top"
                />
              </div>

              <div className="p-6 sm:p-8 flex flex-col">
                <p className="text-[10px] sm:text-xs font-semibold tracking-widest uppercase text-amber-700 mb-2">
                  {t('bfKiniKicker')}
                </p>
                <h3 className="text-2xl sm:text-3xl font-serif text-gray-800 mb-3">
                  {t('bfKiniTitle')}
                </h3>
                <p className="text-gray-500 text-sm mb-4">{t('bfKiniDates')}</p>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-3">
                  {t('bfKiniText1')}
                </p>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {t('bfKiniText2')}
                </p>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes kiniFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes kiniPopIn {
              0% { opacity: 0; transform: translateY(40px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
